import { Format, Progress, RoundRobinCompetition } from './competition';
import { Match } from '@pool-league/shared-util-league';

export const TablePosition = {
  GOLD: 'gold',
  SILVER: 'silver',
  PROMOTION: 'promotion',
  PROMOTION_PLAYOFF: 'promotion-playoff',
  RELEGATION_PLAYOFF: 'relegation-playoff',
  RELEGATION: 'relegation',
} as const;

export type TablePosition = (typeof TablePosition)[keyof typeof TablePosition];

export type Table = Array<{
  playerId: string;
  played: number;
  won: number;
  forfeit: number;
  merit: number;
  position?: TablePosition;
}>;

export interface Playoff {
  seasonId: string;
  format: Format;
  divisionId: number;
  bestOf: number;
  players: [string, string];
  score: [number, number];
  played?: number;
}

export interface Division {
  divisionId: number;
  table: Table;
  playoff?: Playoff;
}

export interface DivisionWithMatches extends Division {
  matches: Match[];
}

export interface EditLeagueResultResponse {
  division: DivisionWithMatches;
  leagueProgress: Progress;
}

export type SetPlayoffPlayersPatchBody = {
  players: [string, string];
};

export type SetPlayoffScorePatchBody = {
  score: [number, number];
};

export type UpdatePlayoffPatchBody =
  | SetPlayoffPlayersPatchBody
  | SetPlayoffScorePatchBody
  | (SetPlayoffPlayersPatchBody & SetPlayoffScorePatchBody);

export function isUpdatePlayoffPatchBody(
  value?: unknown,
): value is UpdatePlayoffPatchBody {
  return (
    !!value &&
    typeof value === 'object' &&
    ('players' in value || 'score' in value) &&
    ('players' in value
      ? Array.isArray(value.players) &&
        value.players.length === 2 &&
        value.players.every((p) => typeof p === 'string')
      : true) &&
    ('score' in value
      ? Array.isArray(value.score) &&
        value.score.length === 2 &&
        value.score.every((s) => typeof s === 'number')
      : true)
  );
}

export type CreateLeaguePutBody = Array<{
  entrants: (string | undefined)[];
  automaticPromotion: number;
  automaticRelegation: number;
  playoff?: {
    bestOf: number;
  };
}>;

export function isCreateLeaguePutBodyItem(
  value?: unknown,
): value is CreateLeaguePutBody[number] {
  return !!value &&
    typeof value === 'object' &&
    'entrants' in value &&
    Array.isArray(value.entrants) &&
    value.entrants.every((e) => typeof e === 'string') &&
    'automaticPromotion' in value &&
    typeof value.automaticPromotion === 'number' &&
    'automaticRelegation' in value &&
    typeof value.automaticRelegation === 'number' &&
    'playoff' in value
    ? typeof value.playoff === 'object' &&
        !!value.playoff &&
        'bestOf' in value.playoff &&
        typeof value.playoff.bestOf === 'number'
    : true;
}

export function isCreateLeaguePutBody(
  value?: unknown,
): value is CreateLeaguePutBody {
  return Array.isArray(value) && value.every(isCreateLeaguePutBodyItem);
}

export interface CreateLeaguePutResponse {
  details: RoundRobinCompetition;
  divisions: Division[];
}
